import type { ReactNode } from "react";
import astrialLogoFullBlack from "./astrial-logo-full-black.png";
import astrialLogoFullWhite from "./astrial-logo-full-white.png";
import astrialLogoMark from "./astrial-logo-mark.png";

export type AstrialLogoVariant = "full" | "mark";

export interface AstrialLogoProps {
  /** `mark` = arrow only (chrome-sized). `full` = arrow + wordmark + tagline + group line. */
  readonly variant?: AstrialLogoVariant;
  readonly className?: string;
  /**
   * Accessible name. Omit for decorative use next to an existing label — the
   * logo then renders `aria-hidden` and announces nothing.
   */
  readonly title?: string;
}

/**
 * Astrial logo, served as the real brand PNG files (bundled from this
 * directory, same as any other imported asset) — not a redrawn or traced
 * copy. `mark` is a pixel crop of the arrow, which is identical in both
 * source files regardless of ink colour, so it needs no theme handling.
 * `full` shows the entire lockup (arrow + wordmark + tagline + "Member of
 * the Satways Ltd. Group") and swaps between the white-ink and black-ink
 * source file per `data-theme`, exactly as specified: white ink for
 * `dark-blue`, black ink for `light`/`grey`/`high-contrast` — see the
 * `.astrial-logo__full` rules in styles.css.
 *
 * Imported (not referenced from `public/`) so the files are hashed into the
 * module graph: `scripts/shell-precache.ts` walks that graph to build the
 * offline service-worker precache list, and a `public/` path would be
 * invisible to it — the logo would break on a repeat offline visit.
 */
export function AstrialLogo({ className, title, variant = "mark" }: AstrialLogoProps): ReactNode {
  if (variant === "mark") {
    return <img alt={title ?? ""} className={className} src={astrialLogoMark} />;
  }
  return (
    <span
      className={className}
      {...(title === undefined
        ? { "aria-hidden": true as const }
        : { role: "img" as const, "aria-label": title })}
    >
      <img alt="" className="astrial-logo__full astrial-logo__full--white" src={astrialLogoFullWhite} />
      <img alt="" className="astrial-logo__full astrial-logo__full--black" src={astrialLogoFullBlack} />
    </span>
  );
}
